// Numbered pagination control. Renders First / Previous / numbered
// pages with ellipsis / Next / Last. Used by the receipts library
// list. Kept dependency-free; a small custom component is cheaper than
// adding a react-paginate or react-table dep.
//
// TODO: cover with component tests once the dashboard adopts a React
// test harness (Vitest + react-testing-library). The behavior to cover:
// first/last/prev/next disabled states, ellipsis placement at the
// boundaries, and that clicks call onPageChange with the right page.

type PaginationProps = {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
};

export function Pagination(props: PaginationProps) {
  const { currentPage, totalItems, pageSize, onPageChange } = props;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  // Hide the control entirely when the whole result set fits on one
  // page; rendering disabled buttons in that case is visual noise.
  if (totalItems <= pageSize) return null;
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const pages = buildPageRanges(safePage, totalPages);
  const atFirst = safePage <= 1;
  const atLast = safePage >= totalPages;
  return (
    <nav className="pagination" aria-label="Pagination">
      <button
        type="button"
        className="btn btn-secondary pagination-btn"
        onClick={() => onPageChange(1)}
        disabled={atFirst}
        aria-label="First page"
      >
        First
      </button>
      <button
        type="button"
        className="btn btn-secondary pagination-btn"
        onClick={() => onPageChange(safePage - 1)}
        disabled={atFirst}
        aria-label="Previous page"
      >
        Previous
      </button>
      <ul className="pagination-pages">
        {pages.map((entry, index) => {
          if (entry === "ellipsis") {
            return (
              <li key={`ellipsis-${index}`} className="pagination-ellipsis" aria-hidden="true">
                …
              </li>
            );
          }
          const isCurrent = entry === safePage;
          return (
            <li key={entry}>
              <button
                type="button"
                className={isCurrent ? "pagination-page is-current" : "pagination-page"}
                onClick={() => onPageChange(entry)}
                aria-current={isCurrent ? "page" : undefined}
              >
                {entry}
              </button>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="btn btn-secondary pagination-btn"
        onClick={() => onPageChange(safePage + 1)}
        disabled={atLast}
        aria-label="Next page"
      >
        Next
      </button>
      <button
        type="button"
        className="btn btn-secondary pagination-btn"
        onClick={() => onPageChange(totalPages)}
        disabled={atLast}
        aria-label="Last page"
      >
        Last
      </button>
    </nav>
  );
}

// Produces the visible page entries: the current page, two before, two
// after, with ellipsis on either end when there are gaps. The first and
// last pages always render so the user can jump there without needing
// the First / Last buttons.
function buildPageRanges(currentPage: number, totalPages: number): Array<number | "ellipsis"> {
  const window = 2;
  const pages = new Set<number>();
  pages.add(1);
  pages.add(totalPages);
  for (let p = currentPage - window; p <= currentPage + window; p++) {
    if (p >= 1 && p <= totalPages) pages.add(p);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const result: Array<number | "ellipsis"> = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push("ellipsis");
    result.push(sorted[i]);
  }
  return result;
}
